import { DeviceModel } from '../../devices/devices.types';
import {
  all,
  call,
  select,
  CallEffect,
  AllEffect,
  SelectEffect,
} from 'redux-saga/effects';
import moment from 'moment';
import { isEmpty, isEqual } from 'lodash';

import { CustomAxios } from '../../../axios';
import { getFactorTestResults } from '../handoff-hub.action';
import { FactorLogicResult, FactorStatus, FactorTest } from '../handoff-hub.reducer';
import { handleGetGreetingsForAllMailboxes } from '../../voice-mailboxes/voice-mailboxes.action';
import { selectVoicemailGreetings } from '../../voice-mailboxes/voice-mailboxes.selectors';

const TOTAL_CALLS_PASS_AMOUNT = 10;
const INBOUND_CALLS_PASS_AMOUNT = 1;
const OUTBOUND_CALLS_PASS_AMOUNT = 1;
const PHONES_COUNT_PASS_AMOUNT = 1;
const VOICEMAIL_GREET_PASS_AMOUNT = 1;

// handlePhonesRegisteredLogic
export const registeredPhonesCriteria = `Must have ${PHONES_COUNT_PASS_AMOUNT} phone(s) registered`;
// handlePhonesCustomizedLogic
export const voicemailCriteria = `Must have ${VOICEMAIL_GREET_PASS_AMOUNT} voicemail greeting(s) recorded`;
export const officeHoursCriteria = `Office Hours must not be the default`;
// handleCallsWorkingLogic
export const inboundCallsCriteria = `Must have received at least ${INBOUND_CALLS_PASS_AMOUNT} inbound call(s) in the last week`;
export const outboundCallsCriteria = `Must have made at least ${OUTBOUND_CALLS_PASS_AMOUNT} outbound call(s) in the last week`;
export const totalCallsCriteria = `Must have total of ${TOTAL_CALLS_PASS_AMOUNT} calls in the last week`;

export const getCallCounts = function* () {
  const response = yield call(CustomAxios.get, '/support/v1/calllogs', {
    params: {
      start: moment().subtract(1, 'week').startOf('day').toISOString(),
      end: moment().endOf('day').toISOString(),
    },
  });
  const calls = response.data.data || [];
  const callCounts = {
    inbound: 0,
    outbound: 0,
  };
  for (const phoneCall of calls) {
    const direction = phoneCall.Direction;
    callCounts[direction]++;
  }

  return callCounts;
};

export const checkCallsWorking = function* (): IterableIterator<
  FactorLogicResult | CallEffect
> {
  const tests: FactorTest[] = [];
  let factorStatus: FactorStatus = 'pass';
  let factorTest: FactorTest | undefined = undefined;

  const callCounts = yield call(getCallCounts);

  [factorTest, factorStatus] = getFactorTestResults(
    factorStatus,
    inboundCallsCriteria,
    // @ts-ignore
    callCounts.inbound,
    // @ts-ignore
    () => callCounts.inbound >= INBOUND_CALLS_PASS_AMOUNT
  );
  tests.push(factorTest);

  [factorTest, factorStatus] = getFactorTestResults(
    factorStatus,
    outboundCallsCriteria,
    // @ts-ignore
    callCounts.outbound,
    // @ts-ignore
    () => callCounts.outbound >= OUTBOUND_CALLS_PASS_AMOUNT
  );
  tests.push(factorTest);
  // @ts-ignore
  const total = callCounts.inbound + callCounts.outbound;

  [factorTest, factorStatus] = getFactorTestResults(
    factorStatus,
    totalCallsCriteria,
    total,
    () => total >= TOTAL_CALLS_PASS_AMOUNT
  );
  tests.push(factorTest);

  return { status: factorStatus, tests };
};

const getMailboxGreetings = function* () {
  yield call(handleGetGreetingsForAllMailboxes);
};

export const checkPhonesCustomized = function* (): IterableIterator<
  FactorLogicResult | CallEffect | AllEffect<CallEffect> | SelectEffect
> {
  const tests: FactorTest[] = [];
  let status: FactorStatus = '';
  let factorStatus: FactorStatus = 'pass';
  let name = '';
  let actual = '';
  let factorTest: FactorTest | undefined = undefined;

  yield call(getMailboxGreetings);
  const greetings = yield select(selectVoicemailGreetings);

  [factorTest, factorStatus] = getFactorTestResults(
    factorStatus,
    voicemailCriteria,
    // @ts-ignore
    greetings.length,
    // @ts-ignore
    () => greetings.length >= VOICEMAIL_GREET_PASS_AMOUNT
  );
  tests.push(factorTest);

  return { status: factorStatus, tests };
};

const getRegisteredPhonesCount = function* () {
  const response = yield call(CustomAxios.get, '/support/v1/phone/devices');
  const devices = response.data.data || [];

  return devices.reduce((count: number, device: DeviceModel) => {
    return device.registration?.addr ? ++count : count;
  }, 0);
};

export const checkPhonesRegistered = function* (): IterableIterator<
  FactorLogicResult | CallEffect
> {
  const tests: FactorTest[] = [];
  let factorStatus: FactorStatus = 'pass';
  let factorTest: FactorTest | undefined = undefined;

  const registeredPhonesCount = yield call(getRegisteredPhonesCount);

  [factorTest, factorStatus] = getFactorTestResults(
    factorStatus,
    registeredPhonesCriteria,
    // @ts-ignore
    registeredPhonesCount,
    // @ts-ignore
    () => registeredPhonesCount >= PHONES_COUNT_PASS_AMOUNT
  );
  tests.push(factorTest);

  return { status: factorStatus, tests };
};
